import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import React, { useEffect } from "react";

import { Box, Typography } from "@mui/material";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import { renderNumberCell, renderPercentCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import { NameRecurrenceType } from "../../../types/Brand";
import { Link } from "react-router-dom";
import { RiExternalLinkLine } from "@remixicon/react";

const RECURRING_NAME_API_URLS: Record<NameRecurrenceType, string> = {
  partially_filled_chains:
    "/brand_tagging/name_recurrences_partially_filled_chains",
};

const RECURRING_NAME_TYPE_LABEL: Record<NameRecurrenceType, string> = {
  partially_filled_chains: "Partially Filled Chains",
};

interface NameRecurrenceSummaryTableProps {
  country_code: string | null;
  name_recurrence_type: NameRecurrenceType | null;
}
const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: {
    sortModel: [{ field: "active_places_without_chain_id", sort: "desc" }],
  },
  pagination: { paginationModel: { pageSize: 250, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "standard",
};

const NameRecurrenceSummaryTable: React.FC<NameRecurrenceSummaryTableProps> = ({
  country_code,
  name_recurrence_type,
}) => {
  const [data, setData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!country_code || !name_recurrence_type) {
        setLoading(false);
        return;
      }

      const api_url = `${
        RECURRING_NAME_API_URLS[name_recurrence_type as NameRecurrenceType]
      }/${country_code}`;

      try {
        const response = await apiRequest<any[]>(api_url, { method: "GET" });

        if (response.data) {
          setData(response.data);
        }
      } catch (error) {
        showError(
          `Failed to fetch data for ${name_recurrence_type} in ${country_code}.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [country_code, name_recurrence_type]);

  const columns_def: GridColDef[] = [
    {
      field: "name_recurrence",
      headerName: "Name Recurrence",
      description: "The name recurrence identifier.",
      type: "string",
      width: 250,
    },
    {
      field: "suggested_chain_id",
      headerName: "Suggested Chain ID",
      description: "The suggested chain identifier.",
      type: "string",
      width: 200,
    },
    {
      field: "total_active_places",
      headerName: "Total Active Places",
      description: "The total number of active places.",
      type: "number",
      width: 180,
      renderCell: renderNumberCell,
    },
    {
      field: "active_places_with_chain_id",
      headerName: "Active Places with Chain ID",
      description: "The number of active places with a chain ID.",
      type: "number",
      width: 220,
      renderCell: renderNumberCell,
    },
    {
      field: "active_places_without_chain_id",
      headerName: "Delta",
      description: "The number of active places without a chain ID.",
      type: "number",
      width: 230,
      renderCell: renderNumberCell,
    },
    {
      field: "percent_without_chain_id",
      headerName: "Percent Delta",
      description: "The percentage of active places without a chain ID.",
      type: "number",
      width: 150,
      renderCell: (params: GridRenderCellParams<Number>) =>
        renderPercentCell(params, 0, "--"),
    },
    {
      field: "review_suggestions",
      headerName: "Review Suggestions",
      width: 250,
      renderCell: (params: any) => {
        return country_code &&
          name_recurrence_type &&
          params.row.name_recurrence &&
          params.row.suggested_chain_id ? (
          <Link
            to={`/data-ops/brands/tagging-suggestions?country=${country_code}&recurrence_type=${name_recurrence_type}&name_like=${encodeURIComponent(
              params.row.name_recurrence
            )}&s_chain_id=${params.row.suggested_chain_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Review <RiExternalLinkLine size="14" />
          </Link>
        ) : (
          "--"
        );
      },
    },
  ];
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {RECURRING_NAME_TYPE_LABEL[name_recurrence_type as NameRecurrenceType]}
      </Typography>
      <GaneshDataGrid
        data={data}
        loading={loading}
        columns={columns_def}
        gridId={`nameRecurrenceTable-${name_recurrence_type}`}
        defaultGridState={defaultGridState}
      />
    </Box>
  );
};

export default NameRecurrenceSummaryTable;
